/**
 * HTTP-клиент к Numerator API.
 *
 * Все запросы шлют initData в `Authorization: tma <initData>` —
 * сервер валидирует HMAC SHA256 с разделителем `\n` (НЕ `&` как у Telegram).
 *
 * См.:
 * - apps/numerator/src/numerator/auth/max_initdata.py — сервер-валидатор.
 * - docs/integrations/mini-app-scanner.md § HMAC валидация initData.
 */

import { PDFDocument } from 'pdf-lib';
import { getInitData } from './max-bridge';

const API_BASE = import.meta.env.VITE_API_BASE || ''; // пусто = same origin (через Vite proxy)

interface UploadScanArgs {
  number: number;
  clientName: string;
  pages: Blob[]; // JPEG-Blob каждой страницы (уже warped/downscaled)
  /**
   * Идемпотентный UUID, генерируется ОДИН раз в App.vue (ref) и переиспользуется
   * на retry. Сервер при повторе вернёт тот же ответ (idempotency table).
   */
  submissionId: string;
}

interface UploadScanResponse {
  number: number;
  scan_url: string;
  ocr_verified: 'confirmed' | 'unconfirmed' | null;
  status: 'claimed' | 'assigned';
  submission_id: string;
  replay: boolean;
}

export async function uploadScan(args: UploadScanArgs): Promise<UploadScanResponse> {
  // 1. Собрать multi-page PDF из JPEG-страниц через pdf-lib (lossless embedJpg)
  const pdfBytes = await buildPdfFromJpegs(args.pages);

  // 2. Multipart POST /api/v1/scans — ВКЛЮЧАЕТ client_name в form-data.
  //    Сервер обновит contract.client_name + scan_url в одной транзакции
  //    (P0.5 fix — устраняет race condition двух последовательных запросов).
  const formData = new FormData();
  formData.append('number', String(args.number));
  formData.append('submission_id', args.submissionId);
  formData.append('client_name', args.clientName);
  formData.append('file', new Blob([pdfBytes], { type: 'application/pdf' }), 'scan.pdf');

  const headers = buildAuthHeaders();

  const response = await fetch(`${API_BASE}/api/v1/scans`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`Upload failed (${response.status}): ${text}`);
  }
  return (await response.json()) as UploadScanResponse;
}

function buildAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const initData = getInitData();
  if (initData) {
    headers['Authorization'] = `tma ${initData}`;
  } else {
    // Dev / mock — fallback на X-Engineer-Id (требует ALLOW_INSECURE_AUTH=true на сервере).
    const mockEngineerId = import.meta.env.VITE_MOCK_ENGINEER_ID;
    if (mockEngineerId) {
      headers['X-Engineer-Id'] = mockEngineerId;
    }
  }
  return headers;
}

/**
 * Перекодировать любой Blob-картинку в JPEG через canvas.
 *
 * 2026-05-27: фикс для бага «The input is not a PNG file!» — iOS Safari
 * иногда отдаёт HEIC / WebP / других «нестандартных» под видом image/jpeg
 * или с пустым blob.type. И embedJpg, и embedPng в pdf-lib падают, потому
 * что magic-байты ни одного из двух форматов не совпадают.
 *
 * Перекодировка через `<img> → <canvas>.toBlob('image/jpeg')` нормализует
 * любой формат, который браузер умеет показывать (включая HEIC на iOS 17+).
 * Качество 0.92 — стандартный sweet-spot для документов.
 */
async function reencodeAsJpeg(blob: Blob): Promise<Uint8Array> {
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('image decode failed'));
      el.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d context unavailable');
    ctx.drawImage(img, 0, 0);
    const jpegBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
        'image/jpeg',
        0.92,
      );
    });
    return new Uint8Array(await jpegBlob.arrayBuffer());
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Определить реальный формат по первым байтам (magic numbers).
 * Возвращает 'jpeg' | 'png' | 'pdf' | 'unknown'.
 *
 * iOS Safari иногда ставит blob.type = 'image/jpeg' даже для HEIC-файлов
 * из «Фото», поэтому полагаться только на MIME нельзя.
 */
function detectMagic(bytes: Uint8Array): 'jpeg' | 'png' | 'pdf' | 'unknown' {
  if (bytes.length < 8) return 'unknown';
  // JPEG: FF D8 FF
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'jpeg';
  // PNG: 89 50 4E 47 0D 0A 1A 0A
  if (
    bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47
    && bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a
  ) return 'png';
  // PDF: %PDF
  if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
    return 'pdf';
  }
  return 'unknown';
}

/**
 * Собрать страницы в один PDF через pdf-lib.
 *
 * Поддерживает 3 типа входных Blob:
 * - JPEG (image/jpeg) — встраивается lossless через embedJpg
 * - PNG  (image/png)  — embedPng
 * - PDF  (application/pdf) — копируем страницы через copyPages
 *   (Microsoft Lens, Apple Notes, Samsung Notes выдают PDF — принимаем как есть)
 *
 * Любой другой формат (HEIC / WebP / неизвестный) — перекодируется в JPEG
 * через canvas. См. reencodeAsJpeg выше.
 */
async function buildPdfFromJpegs(blobs: Blob[]): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.create();

  for (const blob of blobs) {
    let bytes = new Uint8Array(await blob.arrayBuffer());

    // Реальный формат определяем по байтам, не по blob.type.
    // (iOS Safari ставит type='image/jpeg' даже для HEIC-файлов.)
    let magic = detectMagic(bytes);

    if (magic === 'pdf') {
      const src = await PDFDocument.load(bytes);
      const copied = await pdfDoc.copyPages(src, src.getPageIndices());
      copied.forEach((p) => pdfDoc.addPage(p));
      continue;
    }

    if (magic === 'unknown') {
      // HEIC / WebP / что-то ещё — перекодируем через canvas в JPEG.
      console.info(
        `buildPdfFromJpegs: unknown magic for blob (type=${blob.type}, size=${bytes.length}) — re-encoding to JPEG via canvas`,
      );
      try {
        bytes = await reencodeAsJpeg(blob);
        magic = 'jpeg';
      } catch (err) {
        console.error('buildPdfFromJpegs: canvas re-encode failed', err);
        throw new Error(
          `Не удалось обработать страницу (формат не распознан, перекодировка не удалась). `
          + `Попробуйте сделать фото камерой ещё раз.`,
        );
      }
    }

    if (magic === 'png') {
      const image = await pdfDoc.embedPng(bytes);
      const page = pdfDoc.addPage([image.width, image.height]);
      page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });
      continue;
    }

    // JPEG (или перекодированный из HEIC выше).
    const image = await pdfDoc.embedJpg(bytes);
    const page = pdfDoc.addPage([image.width, image.height]);
    page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });
  }
  return pdfDoc.save();
}

interface ReplaceScanArgs {
  number: number;
  reason: string; // опционально, может быть пустым
  pages: Blob[];
  submissionId: string;
}

/**
 * TASK-017: заменить скан существующего договора без смены номера.
 * Номер/дата/ФИО клиента НЕ меняются. Старый PDF на Я.Диске удаляется.
 */
export async function replaceScan(args: ReplaceScanArgs): Promise<UploadScanResponse> {
  const pdfBytes = await buildPdfFromJpegs(args.pages);
  const formData = new FormData();
  formData.append('number', String(args.number));
  formData.append('submission_id', args.submissionId);
  if (args.reason.trim()) {
    formData.append('reason', args.reason.trim());
  }
  formData.append('file', new Blob([pdfBytes], { type: 'application/pdf' }), 'scan.pdf');

  const response = await fetch(`${API_BASE}/api/v1/scans/replace`, {
    method: 'POST',
    headers: buildAuthHeaders(),
    body: formData,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`Replace failed (${response.status}): ${text}`);
  }
  return (await response.json()) as UploadScanResponse;
}

/**
 * Генератор UUID для submission_id. Caller хранит в Vue ref до успешного upload.
 */
export function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback для старых браузеров
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// ─── /api/v1/numbers/my-pending ─────────────────────────────────────────────
// Mini App при открытии узнаёт «для какого номера я открыт» через этот
// endpoint, потому что MAX блокирует передачу параметров через payload и URL
// (ADR-016 revision 2). Endpoint аутентифицируется через initData HMAC.

export interface PendingNumber {
  number: number | null;
  client_name: string | null;
  has_scan: boolean;
  date?: string;
  needs: Array<'client_name' | 'scan'>;
}

export async function fetchMyPending(): Promise<PendingNumber> {
  const response = await fetch(`${API_BASE}/api/v1/numbers/my-pending`, {
    method: 'GET',
    headers: buildAuthHeaders(),
  });
  if (!response.ok) {
    // Сетевая ошибка / 401 — возвращаем null чтобы UI показал привычное
    // «Не указан номер» вместо алярма.
    return { number: null, client_name: null, has_scan: false, needs: [] };
  }
  return (await response.json()) as PendingNumber;
}

// ─── /api/v1/numbers/{n}/upload-info ────────────────────────────────────────
// Перед загрузкой под ПРОИЗВОЛЬНЫЙ номер спрашиваем: существует ли он, мой ли,
// есть ли уже скан (загрузка станет заменой), разрешена ли мне загрузка.

export interface UploadInfo {
  exists: boolean;
  number: number;
  is_owner?: boolean;
  can_upload: boolean;
  has_scan?: boolean;
  client_name?: string | null;
  status?: string;
  engineer_name?: string;
  date?: string;
}

export async function fetchUploadInfo(n: number): Promise<UploadInfo> {
  const response = await fetch(`${API_BASE}/api/v1/numbers/${n}/upload-info`, {
    method: 'GET',
    headers: buildAuthHeaders(),
  });
  if (!response.ok) {
    return { exists: false, number: n, can_upload: false };
  }
  return (await response.json()) as UploadInfo;
}

// ─── whoami (TASK-019 role gate) ───────────────────────────────────────────

export interface WhoAmI {
  engineer_id: string;
  role: EngineerRole | null;
  active: boolean;
  district: string | null;
  full_name_override: string | null;
  is_admin: boolean;
}

export async function fetchWhoAmI(): Promise<WhoAmI | null> {
  try {
    const response = await fetch(`${API_BASE}/api/v1/numbers/whoami`, {
      method: 'GET',
      headers: buildAuthHeaders(),
    });
    if (!response.ok) return null;
    return (await response.json()) as WhoAmI;
  } catch {
    return null;
  }
}

// ─── Admin endpoints (TASK-019) ────────────────────────────────────────────
// Только для роли ADMIN. require_admin_v2 на сервере, 403 для остальных.

export type EngineerRole = 'выездной' | 'менеджер' | 'бухгалтер' | 'админ';

export interface Engineer {
  max_user_id: string;
  role: EngineerRole;
  active: boolean;
  district: string | null;
  full_name_override: string | null;
}

export interface EngineerCreate {
  max_user_id: string;
  role: EngineerRole;
  active?: boolean;
  district?: string | null;
  full_name_override?: string | null;
}

export interface EngineerPatch {
  role?: EngineerRole;
  active?: boolean;
  district?: string | null;
  full_name_override?: string | null;
  clear_district?: boolean;
  clear_full_name_override?: boolean;
}

export interface ContractAdmin {
  number: number;
  date: string;
  engineer_id: string;
  engineer_name: string;
  client_name: string | null;
  district: string | null;
  contract_city?: string | null;  // TASK-027: город договора
  contract_city_source?: string | null;  // ocr/b24/engineer/manual
  status: 'claimed' | 'assigned';
  ocr_verified: 'confirmed' | 'unconfirmed' | null;
  scan_url: string | null;
  beneficiary_engineer_id?: string | null;
  beneficiary_engineer_name?: string | null;
  // TASK-021
  b24_deal_id?: string | null;
  b24_entered_by?: string | null;
  b24_entered_at?: string | null;
}

export interface ContractFilters {
  date_from?: string;
  date_to?: string;
  engineer_id?: string;
  status?: 'claimed' | 'assigned';
  district?: string;
  contract_city?: string;  // TASK-027
  b24?: 'entered' | 'missing';  // TASK-021
  limit?: number;
}

export interface ContractPatch {
  client_name?: string;
  date?: string;
  district?: string;
  contract_city?: string;  // TASK-027: ручная правка города договора
  engineer_id?: string;
  // TASK-020
  beneficiary_engineer_id?: string;
  clear_beneficiary?: boolean;
  // TASK-021
  b24_deal_id?: string;
  clear_b24?: boolean;
}

async function adminFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...buildAuthHeaders(),
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers || {}),
    },
  });
  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`${response.status}: ${text}`);
  }
  return (await response.json()) as T;
}

// Engineers
export const adminListEngineers = (onlyActive = false) =>
  adminFetch<Engineer[]>(
    `/api/v1/admin/engineers?only_active=${onlyActive}`,
  );

export const adminCreateEngineer = (payload: EngineerCreate) =>
  adminFetch<Engineer>('/api/v1/admin/engineers', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const adminPatchEngineer = (
  maxUserId: string,
  payload: EngineerPatch,
) =>
  adminFetch<Engineer>(
    `/api/v1/admin/engineers/${encodeURIComponent(maxUserId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );

export const adminListDistricts = () =>
  adminFetch<string[]>('/api/v1/admin/districts');

// Contracts
export const adminListContracts = (filters: ContractFilters = {}) => {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '') {
      params.append(k, String(v));
    }
  }
  const qs = params.toString();
  return adminFetch<ContractAdmin[]>(
    `/api/v1/admin/contracts${qs ? `?${qs}` : ''}`,
  );
};

export const adminGetContract = (number: number) =>
  adminFetch<ContractAdmin>(`/api/v1/admin/contracts/${number}`);

export const adminPatchContract = (number: number, payload: ContractPatch) =>
  adminFetch<ContractAdmin>(`/api/v1/admin/contracts/${number}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

// TASK-030: удаление договора (только админ). Со сканом → полное удаление + PDF
// с Я.Диска; без скана → эквивалент отмены. Номер возвращается в reissue.
export const adminDeleteContract = (number: number, reason?: string) => {
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : '';
  return adminFetch<{ number: number; deleted: boolean; had_scan: boolean }>(
    `/api/v1/admin/contracts/${number}${qs}`,
    { method: 'DELETE' },
  );
};

// ─── Audit (TASK-007) ──────────────────────────────────────────────────────

export interface AuditEntry {
  id: number;
  actor_user_id: string;
  actor_role: string;
  action: string;
  target_number: number | null;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface AuditFilters {
  number?: number;
  actor?: string;
  action?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
}

export const adminListAudit = (filters: AuditFilters = {}) => {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '') {
      params.append(k, String(v));
    }
  }
  const qs = params.toString();
  return adminFetch<AuditEntry[]>(
    `/api/v1/admin/audit${qs ? `?${qs}` : ''}`,
  );
};

export const adminListAuditActions = () =>
  adminFetch<string[]>('/api/v1/admin/audit/actions');

// TASK-023 W3: OCR metrics — accuracy per (engine, field) за N дней.

export interface OcrMetricRow {
  engine: string;          // 'tesseract' / 'yandex_vision' / 'gigachat'
  field_name: string;      // 'client_name' / 'phone' / 'address' / 'amount'
  total_samples: number;
  with_ground_truth: number;
  recall: number | null;
  avg_similarity: number | null;
  exact_match_rate: number | null;
}

export interface OcrMetricsOut {
  days: number;
  total_samples: number;
  rows: OcrMetricRow[];
  engine_versions: { engine: string; version: string | null }[];
}

export const adminGetOcrMetrics = (days = 30) =>
  adminFetch<OcrMetricsOut>(`/api/v1/admin/ocr-metrics?days=${days}`);


// TASK-006 W1: Magic-link PWA auth.

export interface PwaLinkStartResp {
  token: string;
  deeplink: string;
  expires_in: number;
}

export interface MeResp {
  max_user_id: string;
  role: string | null;
  full_name: string | null;
  is_admin: boolean;
}

// Используем существующий API_BASE сверху файла

/** Создать magic-link токен и получить deeplink для открытия MAX. */
export async function pwaLinkStart(): Promise<PwaLinkStartResp> {
  const r = await fetch(`${API_BASE}/api/v1/auth/pwa-link/start`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!r.ok) throw new Error(`pwa-link/start ${r.status}`);
  return r.json();
}

/** Опросить статус токена. 202=pending, 200=залогинены (cookie выдан), 410=expired. */
export async function pwaLinkPoll(token: string): Promise<'pending' | 'ok' | 'expired'> {
  const r = await fetch(
    `${API_BASE}/api/v1/auth/pwa-link/poll?token=${encodeURIComponent(token)}`,
    { credentials: 'include' },
  );
  if (r.status === 202) return 'pending';
  if (r.status === 200) return 'ok';
  return 'expired';
}

/** Кто я по cookie. 401 если cookie нет/невалидна. */
export async function meByCookie(): Promise<MeResp> {
  const r = await fetch(`${API_BASE}/api/v1/auth/me`, { credentials: 'include' });
  if (!r.ok) throw new Error(`auth/me ${r.status}`);
  return r.json();
}

export async function logout(): Promise<void> {
  await fetch(`${API_BASE}/api/v1/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  });
}
